import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import '../styles/Services.css';

const Services = ({ translations, language }) => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  const servicesData = {
    en: {
      title: 'Services',
      subtitle: 'Futuristic Marketing Solutions',
      services: [
        {
          icon: 'fas fa-search',
          title: 'SEO & Content Strategy',
          description: 'Advanced SEO techniques with AI-powered content optimization to dominate search rankings.',
          features: ['Technical SEO Audit', 'Content Gap Analysis', 'AI Content Optimization', 'Local SEO'],
          color: '#8b5cf6'
        },
        {
          icon: 'fas fa-bullhorn',
          title: 'Paid Advertising',
          description: 'Data-driven PPC campaigns across Google, Meta, and emerging platforms for maximum ROI.',
          features: ['Google Ads Management', 'Meta Ads Optimization', 'YouTube Advertising', 'Retargeting Campaigns'],
          color: '#06b6d4'
        },
        {
          icon: 'fas fa-share-alt',
          title: 'Social Media Marketing',
          description: 'Engaging social media strategies that build communities and drive conversions.',
          features: ['Content Creation', 'Community Management', 'Influencer Partnerships', 'Social Commerce'],
          color: '#f5576c'
        },
        {
          icon: 'fas fa-chart-line',
          title: 'Funnel Optimization',
          description: 'Conversion-focused funnel design and optimization using advanced analytics and A/B testing.',
          features: ['Landing Page Design', 'A/B Testing', 'User Journey Mapping', 'CRO Analysis'],
          color: '#10b981'
        },
        {
          icon: 'fas fa-envelope',
          title: 'Email Marketing',
          description: 'Automated email sequences and personalized campaigns that nurture leads into customers.',
          features: ['Email Automation', 'Segmentation', 'Personalization', 'Performance Tracking'],
          color: '#f59e0b'
        },
        {
          icon: 'fas fa-brain',
          title: 'AI Marketing Solutions',
          description: 'Cutting-edge AI tools and strategies to automate and optimize your marketing efforts.',
          features: ['Predictive Analytics', 'Chatbot Integration', 'AI Content Generation', 'Smart Bidding'],
          color: '#8b5cf6'
        }
      ]
    },
    ar: {
      title: 'الخدمات',
      subtitle: 'حلول تسويقية مستقبلية',
      services: [
        {
          icon: 'fas fa-search',
          title: 'تحسين محركات البحث واستراتيجية المحتوى',
          description: 'تقنيات متقدمة لتحسين محركات البحث مع تحسين المحتوى بالذكاء الاصطناعي للهيمنة على تصنيفات البحث.',
          features: ['تدقيق تقني لتحسين محركات البحث', 'تحليل فجوات المحتوى', 'تحسين المحتوى بالذكاء الاصطناعي', 'تحسين محركات البحث المحلية'],
          color: '#8b5cf6'
        },
        {
          icon: 'fas fa-bullhorn',
          title: 'الإعلانات المدفوعة',
          description: 'حملات إعلانية مدفوعة مدفوعة بالبيانات عبر جوجل وميتا والمنصات الناشئة لتحقيق أقصى عائد استثمار.',
          features: ['إدارة إعلانات جوجل', 'تحسين إعلانات ميتا', 'إعلانات يوتيوب', 'حملات إعادة الاستهداف'],
          color: '#06b6d4'
        },
        {
          icon: 'fas fa-share-alt',
          title: 'تسويق وسائل التواصل الاجتماعي',
          description: 'استراتيجيات وسائل التواصل الاجتماعي الجذابة التي تبني المجتمعات وتحفز التحويلات.',
          features: ['إنشاء المحتوى', 'إدارة المجتمع', 'شراكات المؤثرين', 'التجارة الاجتماعية'],
          color: '#f5576c'
        },
        {
          icon: 'fas fa-chart-line',
          title: 'تحسين القمع',
          description: 'تصميم وتحسين القمع المركز على التحويل باستخدام التحليلات المتقدمة واختبار A/B.',
          features: ['تصميم صفحات الهبوط', 'اختبار A/B', 'رسم خريطة رحلة المستخدم', 'تحليل تحسين معدل التحويل'],
          color: '#10b981'
        },
        {
          icon: 'fas fa-envelope',
          title: 'التسويق عبر البريد الإلكتروني',
          description: 'تسلسلات بريد إلكتروني آلية وحملات شخصية تغذي العملاء المحتملين إلى عملاء.',
          features: ['أتمتة البريد الإلكتروني', 'التجزئة', 'التخصيص', 'تتبع الأداء'],
          color: '#f59e0b'
        },
        {
          icon: 'fas fa-brain',
          title: 'حلول التسويق بالذكاء الاصطناعي',
          description: 'أدوات واستراتيجيات ذكاء اصطناعي متطورة لأتمتة وتحسين جهودك التسويقية.',
          features: ['التحليلات التنبؤية', 'تكامل الشات بوت', 'توليد المحتوى بالذكاء الاصطناعي', 'المزايدة الذكية'],
          color: '#8b5cf6'
        }
      ]
    }
  };

  const data = servicesData[language];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <section id="services" className="services-section section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title gradient-text">{data.title}</h2>
          <p className="section-subtitle">{data.subtitle}</p>
        </motion.div>

        <motion.div
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {data.services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card glass-morphism"
              variants={itemVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
            >
              <div className="service-header">
                <motion.div 
                  className="service-icon"
                  style={{ color: service.color }}
                  whileHover={{ 
                    scale: 1.2,
                    rotate: 360,
                    transition: { duration: 0.5 }
                  }}
                >
                  <i className={service.icon}></i>
                </motion.div>
                <h3 className="service-title">{service.title}</h3>
              </div>
              
              <p className="service-description">{service.description}</p>
              
              <div className="service-features">
                {service.features.map((feature, featureIndex) => (
                  <motion.div
                    key={featureIndex}
                    className="feature-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ 
                      duration: 0.5, 
                      delay: 0.8 + index * 0.1 + featureIndex * 0.1 
                    }}
                  >
                    <i className="fas fa-check" style={{ color: service.color }}></i>
                    <span>{feature}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div
                className="service-glow"
                style={{ background: `radial-gradient(circle, ${service.color}20 0%, transparent 70%)` }}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              ></motion.div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="services-cta"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <motion.button
            className="btn-futuristic primary"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            {language === 'en' ? 'Get Custom Strategy' : 'احصل على استراتيجية مخصصة'}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;